/**
 * Standard technical indicator calculations matching TradingView/StockCharts formulas.
 * All functions take an array of OHLCV objects and return a new array with calculated values.
 */

// Helper: Simple Moving Average over a window
function sma(values, period) {
  const result = new Array(values.length).fill(undefined);
  for (let i = period - 1; i < values.length; i++) {
    let sum = 0;
    for (let j = i - period + 1; j <= i; j++) {
      sum += values[j];
    }
    result[i] = sum / period;
  }
  return result;
}

// Helper: Exponential Moving Average
function ema(values, period) {
  const result = new Array(values.length).fill(undefined);
  const k = 2 / (period + 1);

  // First EMA value is SMA of first `period` values
  let sum = 0;
  for (let i = 0; i < period && i < values.length; i++) {
    if (values[i] == null) return result;
    sum += values[i];
  }
  result[period - 1] = sum / period;

  for (let i = period; i < values.length; i++) {
    if (values[i] == null) continue;
    result[i] = values[i] * k + result[i - 1] * (1 - k);
  }
  return result;
}

// Helper: Wilder's smoothing (used in RSI, ADX, ATR)
function wilderSmooth(values, period) {
  const result = new Array(values.length).fill(undefined);

  // First value is SMA
  let sum = 0;
  let count = 0;
  for (let i = 0; i < period && i < values.length; i++) {
    if (values[i] != null) {
      sum += values[i];
      count++;
    }
  }
  if (count < period) return result;
  result[period - 1] = sum / period;

  for (let i = period; i < values.length; i++) {
    if (values[i] == null) continue;
    result[i] = (result[i - 1] * (period - 1) + values[i]) / period;
  }
  return result;
}

/**
 * CCI - Commodity Channel Index
 * Formula: (TP - SMA(TP, period)) / (0.015 * Mean Deviation)
 * TP = (High + Low + Close) / 3
 */
export function calculateCCI(data, period = 20) {
  return data.map((d, i, arr) => {
    if (i < period - 1) return { ...d, CCI: undefined };

    // Typical prices for the window
    const tps = [];
    for (let j = i - period + 1; j <= i; j++) {
      tps.push((arr[j].high + arr[j].low + arr[j].close) / 3);
    }

    const tp = tps[tps.length - 1];
    const smaTP = tps.reduce((a, b) => a + b, 0) / period;
    const meanDeviation = tps.reduce((a, b) => a + Math.abs(b - smaTP), 0) / period;

    const cci = meanDeviation === 0 ? 0 : (tp - smaTP) / (0.015 * meanDeviation);
    return { ...d, CCI: cci };
  });
}

/**
 * OBV - On-Balance Volume
 * If close > prevClose: OBV += volume
 * If close < prevClose: OBV -= volume
 * If close == prevClose: OBV unchanged
 */
export function calculateOBV(data) {
  let obv = 0;
  return data.map((d, i, arr) => {
    if (i === 0) {
      obv = 0;
    } else {
      if (d.close > arr[i - 1].close) {
        obv += (d.volume || 0);
      } else if (d.close < arr[i - 1].close) {
        obv -= (d.volume || 0);
      }
    }
    return { ...d, OBV: obv };
  });
}

/**
 * ROC - Rate of Change
 * Formula: ((Close - Close[period]) / Close[period]) * 100
 */
export function calculateROC(data, period = 12) {
  return data.map((d, i, arr) => {
    if (i < period) return { ...d, ROC: undefined };
    const prevClose = arr[i - period].close;
    const roc = prevClose === 0 ? 0 : ((d.close - prevClose) / prevClose) * 100;
    return { ...d, ROC: roc };
  });
}

/**
 * MFI - Money Flow Index
 * Like RSI but uses money flow (TP * volume) instead of price
 */
export function calculateMFI(data, period = 14) {
  // Calculate typical prices and raw money flow
  const tps = data.map(d => (d.high + d.low + d.close) / 3);
  const rawMF = data.map((d, i) => tps[i] * (d.volume || 0));

  return data.map((d, i) => {
    if (i < period) return { ...d, MFI: undefined };

    let positiveMF = 0;
    let negativeMF = 0;

    for (let j = i - period + 1; j <= i; j++) {
      if (tps[j] > tps[j - 1]) {
        positiveMF += rawMF[j];
      } else {
        negativeMF += rawMF[j];
      }
    }

    const mfi = negativeMF === 0 ? 100 : 100 - (100 / (1 + positiveMF / negativeMF));
    return { ...d, MFI: mfi };
  });
}

/**
 * ADX - Average Directional Index (with +DI and -DI)
 * Uses Wilder's smoothing method
 */
export function calculateADX(data, period = 14) {
  if (data.length < 2) return data.map(d => ({ ...d, ADX: undefined, DMP: undefined, DMN: undefined }));

  // Step 1: Calculate True Range, +DM, -DM
  const tr = [0];
  const plusDM = [0];
  const minusDM = [0];

  for (let i = 1; i < data.length; i++) {
    const high = data[i].high;
    const low = data[i].low;
    const prevHigh = data[i - 1].high;
    const prevLow = data[i - 1].low;
    const prevClose = data[i - 1].close;

    tr.push(Math.max(high - low, Math.abs(high - prevClose), Math.abs(low - prevClose)));

    const upMove = high - prevHigh;
    const downMove = prevLow - low;
    plusDM.push(upMove > downMove && upMove > 0 ? upMove : 0);
    minusDM.push(downMove > upMove && downMove > 0 ? downMove : 0);
  }

  // Step 2: Wilder's smoothing
  const smoothTR = wilderSmooth(tr, period);
  const smoothPlusDM = wilderSmooth(plusDM, period);
  const smoothMinusDM = wilderSmooth(minusDM, period);

  // Step 3: +DI, -DI, DX
  const plusDI = new Array(data.length).fill(undefined);
  const minusDI = new Array(data.length).fill(undefined);
  const dx = new Array(data.length).fill(undefined);

  for (let i = period - 1; i < data.length; i++) {
    if (smoothTR[i] == null || smoothTR[i] === 0) continue;
    plusDI[i] = (smoothPlusDM[i] / smoothTR[i]) * 100;
    minusDI[i] = (smoothMinusDM[i] / smoothTR[i]) * 100;
    const diSum = plusDI[i] + minusDI[i];
    dx[i] = diSum === 0 ? 0 : (Math.abs(plusDI[i] - minusDI[i]) / diSum) * 100;
  }

  // Step 4: ADX = Wilder's smoothing of DX
  const adx = wilderSmooth(dx.map((v, i) => i >= period - 1 ? v : undefined).filter(v => v != null).length > 0
    ? dx : new Array(data.length).fill(undefined), period);

  // For ADX, we need to smooth DX starting from where DX is first defined (at period-1)
  // Re-calculate properly
  const adxValues = new Array(data.length).fill(undefined);
  let firstDXIdx = -1;
  for (let i = 0; i < dx.length; i++) {
    if (dx[i] != null) { firstDXIdx = i; break; }
  }

  if (firstDXIdx >= 0 && firstDXIdx + period - 1 < data.length) {
    // First ADX = SMA of first `period` DX values
    let dxSum = 0;
    let dxCount = 0;
    for (let i = firstDXIdx; i < firstDXIdx + period && i < data.length; i++) {
      if (dx[i] != null) {
        dxSum += dx[i];
        dxCount++;
      }
    }
    if (dxCount === period) {
      const startIdx = firstDXIdx + period - 1;
      adxValues[startIdx] = dxSum / period;
      for (let i = startIdx + 1; i < data.length; i++) {
        if (dx[i] != null && adxValues[i - 1] != null) {
          adxValues[i] = (adxValues[i - 1] * (period - 1) + dx[i]) / period;
        }
      }
    }
  }

  return data.map((d, i) => ({
    ...d,
    ADX: adxValues[i] != null ? adxValues[i] : undefined,
    DMP: plusDI[i] != null ? plusDI[i] : undefined,
    DMN: minusDI[i] != null ? minusDI[i] : undefined,
  }));
}

/**
 * CMF - Chaikin Money Flow
 * MFM = ((Close - Low) - (High - Close)) / (High - Low)
 * MFV = MFM * Volume
 * CMF = Sum(MFV, period) / Sum(Volume, period)
 */
export function calculateCMF(data, period = 20) {
  return data.map((d, i, arr) => {
    if (i < period - 1) return { ...d, CMF: undefined };

    let mfvSum = 0;
    let volSum = 0;

    for (let j = i - period + 1; j <= i; j++) {
      const bar = arr[j];
      const range = bar.high - bar.low;
      const mfm = range === 0 ? 0 : ((bar.close - bar.low) - (bar.high - bar.close)) / range;
      mfvSum += mfm * (bar.volume || 0);
      volSum += (bar.volume || 0);
    }

    const cmf = volSum === 0 ? 0 : mfvSum / volSum;
    return { ...d, CMF: cmf };
  });
}

/**
 * EFI - Elder Force Index
 * Force Index = (Close - PrevClose) * Volume
 * EFI = EMA(Force Index, period)
 */
export function calculateEFI(data, period = 13) {
  // Raw force index
  const fi = data.map((d, i, arr) => {
    if (i === 0) return 0;
    return (d.close - arr[i - 1].close) * (d.volume || 0);
  });

  // EMA smoothing
  const efiValues = ema(fi, period);

  return data.map((d, i) => ({
    ...d,
    EFI: efiValues[i] != null ? efiValues[i] : undefined,
  }));
}

/**
 * PPO - Percentage Price Oscillator
 * PPO = ((EMA(Close, fast) - EMA(Close, slow)) / EMA(Close, slow)) * 100
 * Signal = EMA(PPO, signal)
 * Histogram = PPO - Signal
 */
export function calculatePPO(data, fast = 12, slow = 26, signal = 9) {
  const closes = data.map(d => d.close);
  const emaFast = ema(closes, fast);
  const emaSlow = ema(closes, slow);

  const ppoValues = data.map((d, i) => {
    if (emaFast[i] == null || emaSlow[i] == null || emaSlow[i] === 0) return undefined;
    return ((emaFast[i] - emaSlow[i]) / emaSlow[i]) * 100;
  });

  const signalValues = ema(ppoValues.map(v => v != null ? v : undefined), signal);

  return data.map((d, i) => ({
    ...d,
    PPO: ppoValues[i] != null ? ppoValues[i] : undefined,
    PPOs: signalValues[i] != null ? signalValues[i] : undefined,
    PPOh: ppoValues[i] != null && signalValues[i] != null ? ppoValues[i] - signalValues[i] : undefined,
  }));
}

/**
 * Stochastic Oscillator
 * %K = ((Close - Lowest Low) / (Highest High - Lowest Low)) * 100 over kPeriod, smoothed by kSlowing
 * %D = SMA(%K, dPeriod)
 */
export function calculateStochastic(data, kPeriod = 14, kSlowing = 3, dPeriod = 3) {
  // Raw %K
  const rawK = data.map((d, i, arr) => {
    if (i < kPeriod - 1) return undefined;
    let lowestLow = Infinity;
    let highestHigh = -Infinity;
    for (let j = i - kPeriod + 1; j <= i; j++) {
      if (arr[j].low < lowestLow) lowestLow = arr[j].low;
      if (arr[j].high > highestHigh) highestHigh = arr[j].high;
    }
    const range = highestHigh - lowestLow;
    return range === 0 ? 50 : ((d.close - lowestLow) / range) * 100;
  });

  // Slow %K = SMA(raw %K, kSlowing)
  const slowK = sma(rawK.map(v => v != null ? v : 0), kSlowing).map((v, i) => rawK[i] != null && i >= kPeriod - 1 + kSlowing - 1 ? v : undefined);

  // %D = SMA(slow %K, dPeriod)
  const stochD = sma(slowK.map(v => v != null ? v : 0), dPeriod).map((v, i) => slowK[i] != null ? v : undefined);

  return data.map((d, i) => ({
    ...d,
    STOCHk: slowK[i] != null ? slowK[i] : undefined,
    STOCHd: stochD[i] != null ? stochD[i] : undefined,
  }));
}

/**
 * PVO - Percentage Volume Oscillator
 * Same as PPO but uses Volume instead of Close
 */
export function calculatePVO(data, fast = 12, slow = 26, signal = 9) {
  const volumes = data.map(d => d.volume || 0);
  const emaFast = ema(volumes, fast);
  const emaSlow = ema(volumes, slow);

  const pvoValues = data.map((d, i) => {
    if (emaFast[i] == null || emaSlow[i] == null || emaSlow[i] === 0) return undefined;
    return ((emaFast[i] - emaSlow[i]) / emaSlow[i]) * 100;
  });

  const signalValues = ema(pvoValues.map(v => v != null ? v : undefined), signal);

  return data.map((d, i) => ({
    ...d,
    PVO: pvoValues[i] != null ? pvoValues[i] : undefined,
    PVOs: signalValues[i] != null ? signalValues[i] : undefined,
    PVOh: pvoValues[i] != null && signalValues[i] != null ? pvoValues[i] - signalValues[i] : undefined,
  }));
}
